import { Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useOneDriveFileUpload } from '../hooks/file-uploads/useOneDriveFileUpload';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import { useUpdateFileDocumentDate } from '../hooks/useUpdateFileDocumentDate';
import { useDetectedEntitiesStore } from '../stores/useDetectedEntitiesStore';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import { dataUploadFileProperties, Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useAllowedFileTypes } from './allowedFileTypes';
import FileUploadLayout from './FileUploadLayout';
import AddDocumentsList from './components/AddDocumentsList';
import { useSharepointUpload } from './hooks/useSharepointUpload';
import { FiledInfo } from './components/AddDocumentRow';
import DocumentsDropZone from './components/DocumentsDropZone';
import DocumentsUploadLanding from './components/DocumentsUploadLanding';
import MoveFileModal from './files-table/MoveFileModal';
const FILED_ROW_DURATION_MS = 3000;

export default function AddDocumentsPage() {
  const { clientId, client } = useCurrentClient();
  const { extensions, fileButtonAccept } = useAllowedFileTypes();
  const navigate = useNavigate();
  const { openPicker, pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();
  const clearExpiredFiles = useDetectedEntitiesStore(state => state.clearExpiredFiles);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filedTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const rootFolderLabel = `All ${client?.firstName || 'client'}'s documents`;

  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [filedInfo, setFiledInfo] = useState<Record<string, FiledInfo>>({});

  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([]);

  const {
    files,
    filesToAdd,
    deleteFile,
    moveFile,
    refetchFiles,
    uploadFiles,
    retryProcessing,
    setFileResolved,
    isLoading,
    error,
  } = useFileUploads({ autoRefreshProcessing: true });

  const [retryingFileId, setRetryingFileId] = useState<string | null>(null);

  const { flagEnabledSharepointUpload } = useFlags();
  const { openPicker: openSharepointPicker } = useSharepointUpload({
    clientId,
    onComplete: () => {},
  });
  const { uploadOneDriveFiles } = useOneDriveFileUpload();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const { updateDocumentDate } = useUpdateFileDocumentDate();

  useEffect(() => {
    clearExpiredFiles();
  }, []);

  useEffect(() => {
    const timers = filedTimersRef.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      Logger.info('Processing pending OneDrive items from global provider', { pendingPickedItems });
      uploadOneDriveFilesDirectly(pendingPickedItems);
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, clearPendingPickedItems]);

  const addPendingFiles = (fileNames: { name: string; size: number; type: string }[]) => {
    const newFiles: UploadedFile[] = fileNames.map(f => ({
      id: `pending-${uuidv4()}`,
      file: {
        id: 0,
        fileName: f.name,
        fileSize: f.size,
        fileType: f.type,
        isFolder: false,
        isResolved: false,
        isFiled: false,
        isTemporary: false,
        createdAt: new Date().toISOString(),
      },
      isFolder: false,
      isResolved: false,
      createdAt: new Date().toISOString(),
      detectedClients: [],
      backgroundProcess: {
        id: '',
        processName: '',
        status: 'PENDING' as const,
        statusMessage: '',
        extraDetails: null,
      },
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
    return newFiles;
  };

  const uploadWithOptimisticUI = <T,>(
    items: T[],
    toPendingInfo: (item: T) => { name: string; size: number; type: string },
    doUpload: () => Promise<UploadedFile[]>,
  ) => {
    const pending = addPendingFiles(items.map(toPendingInfo));
    const pendingIds = new Set(pending.map(f => f.id));
    doUpload()
      .then(uploadedFiles => {
        uploadedFiles.forEach(file => {
          Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_UPLOADED, dataUploadFileProperties(file));
        });
        dispatchAppEvent('DATA_UPLOADED');
      })
      .catch(error => {
        notifyAndLogError(
          'Failed to upload files',
          'An error occurred while uploading the files. Please try again later.',
          error,
          { clientId, fileCount: items.length },
        );
      })
      .finally(() => {
        refetchFiles()
          .catch(() => {})
          .finally(() => {
            setPendingFiles(prev => prev.filter(f => !pendingIds.has(f.id)));
          });
      });
  };

  const uploadFilesDirectly = (files: File[]) => {
    uploadWithOptimisticUI(
      files,
      f => ({ name: f.name, size: f.size, type: f.type }),
      () =>
        uploadFiles(files.map(file => ({ file, modelId: undefined, documentDate: null }))).then(
          results => results.map(r => r.data),
        ),
    );
  };

  const uploadOneDriveFilesDirectly = (items: PickedItem[]) => {
    uploadWithOptimisticUI(
      items,
      i => ({ name: i.name || 'Unknown', size: i.size || 0, type: '' }),
      () => uploadOneDriveFiles({ items, documentDate: null, parentId: null }),
    );
  };

  const handleSharePointClick = (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    openPicker(items => {
      Logger.info('picked items from OneDrive:', { items });
      uploadOneDriveFilesDirectly(items);
    }, extensions);
  };

  const handleSelectedInputFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) uploadFilesDirectly(files);
    event.target.value = '';
  };

  const handleUploadToSharepoint = (file: UploadedFile) => {
    openSharepointPicker([{ fileId: file.file.id, fileName: file.file.fileName }]);
  };

  const handleDownloadFile = async (file: UploadedFile) => {
    try {
      const link = await createLink(file.file.id);
      window.open(link.url, '_blank');
    } catch (error) {
      notifyAndLogError(
        'Failed to open file',
        'An error occurred while trying to open the file. Please try again later.',
        error,
        { clientId, fileId: file.file.id },
      );
    }
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      await deleteFile(file.id);
    } catch (error) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const handleUpdateDocumentDate = async (file: UploadedFile, date: Date) => {
    try {
      await updateDocumentDate(
        clientId,
        file.id,
        date,
        file.isResolved,
        file.parentId,
        file.file.isFiled,
      );
    } catch (error) {
      notifyAndLogError(
        'Failed to update date',
        'An error occurred while trying to update the date. Please try again later.',
        error,
        { clientId, dataUploadFileId: file.id },
      );
    }
  };

  const handleProcessFile = (file: UploadedFile, detectedClientId?: string) => {
    Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_PROCESS_CLICK, dataUploadFileProperties(file));
    navigate(
      `/client/${detectedClientId ?? clientId}/detected-entities?fileId=${file.id}&fileName=${file.file.fileName}&guid=${uuidv4()}`,
    );
  };

  const handleRetryProcessing = async (file: UploadedFile) => {
    setRetryingFileId(file.id);
    try {
      await retryProcessing(file);
    } catch (error) {
      notifyAndLogError(
        'Failed to retry processing',
        'An error occurred while retrying. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    } finally {
      setRetryingFileId(null);
    }
  };

  const handleSetResolved = async (file: UploadedFile, isResolved: boolean) => {
    try {
      Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_SET_RESOLVED, dataUploadFileProperties(file));
      await setFileResolved(file, isResolved);
    } catch (error) {
      notifyAndLogError(
        'Failed to mark file as reviewed',
        'An error occurred while trying to mark the file as reviewed. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const handleFileTo = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);

      const folderName = parentId
        ? files.find(f => f.id === parentId && f.isFolder)?.file.fileName || rootFolderLabel
        : rootFolderLabel;
      const existingCount = files.filter(
        f => f.id !== file.id && !f.isFolder && (f.parentId ?? null) === (parentId ?? null),
      ).length;

      setFiledInfo(prev => ({
        ...prev,
        [file.id]: {
          folderName,
          count: existingCount + 1,
          folderId: parentId ?? null,
        },
      }));

      const timer = setTimeout(() => {
        setFiledInfo(prev => {
          const next = { ...prev };
          delete next[file.id];
          return next;
        });
        filedTimersRef.current.delete(file.id);
        refetchFiles().catch(() => {});
      }, FILED_ROW_DURATION_MS);
      filedTimersRef.current.set(file.id, timer);
    } catch (error) {
      notifyAndLogError(
        'Failed to file document',
        'An error occurred while trying to file the document. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const handleOpenFiledFolder = (info: FiledInfo) => {
    const path = info.folderId
      ? `/client/${clientId}/document/files/folder/${info.folderId}`
      : `/client/${clientId}/document/files`;
    navigate(path);
  };

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isLoading || isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  const uploadedFileNames = new Set(filesToAdd.map(f => f.file.fileName));
  const allFiles = [
    ...filesToAdd,
    ...pendingFiles.filter(f => !uploadedFileNames.has(f.file.fileName)),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <>
      <FileUploadLayout title="Add documents" hideHeader>
        {allFiles.length === 0 ? (
          <>
            <input
              type="file"
              multiple
              accept={fileButtonAccept}
              hidden
              ref={fileInputRef}
              onChange={handleSelectedInputFiles}
            />
            <DocumentsUploadLanding
              onUploadDocument={() => fileInputRef.current?.click()}
              onUploadFromSharePoint={handleSharePointClick}
            />
          </>
        ) : (
          <Stack gap="xxl">
            <DocumentsDropZone
              onFilesSelected={uploadFilesDirectly}
              onSharePointClick={handleSharePointClick}
            />
            <AddDocumentsList
              files={allFiles}
              filedInfo={filedInfo}
              rootFolderLabelFor={() => rootFolderLabel}
              contactNameFor={() => (client ? `${client.firstName} ${client.lastName}` : null)}
              retryingFileId={retryingFileId}
              onDownload={handleDownloadFile}
              onDelete={handleDeleteFile}
              onUpdateDate={handleUpdateDocumentDate}
              onProcess={handleProcessFile}
              onRetry={handleRetryProcessing}
              onFileTo={handleFileTo}
              onOpenFiledFolder={handleOpenFiledFolder}
              onSetResolved={handleSetResolved}
              onUploadToSharepoint={
                flagEnabledSharepointUpload ? handleUploadToSharepoint : undefined
              }
            />
          </Stack>
        )}
      </FileUploadLayout>

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={files}
          rootLabel={rootFolderLabel}
          onFileMoved={handleMoveFile}
        />
      )}
    </>
  );
}
